<script setup lang="ts">
  import { toTypedSchema } from '@vee-validate/zod';
  import { storeToRefs } from 'pinia';
  import { useForm } from 'vee-validate';
  import { inject, onBeforeMount, ref } from 'vue';
  import * as zod from 'zod';

  import type { Partner } from '@stuut/api/models';
  import { Contact } from '@stuut/api/models';
  import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
  } from '@ui/components/alert-dialog';
  import { Card, CardContent, CardTitle } from '@ui/components/card';
  import { FormControl, FormField, FormItem, FormLabel } from '@ui/components/form';
  import { Input } from '@ui/components/input';
  import { toast } from '@ui/components/sonner';

  import { stuut } from '@/api';
  import ContactCards from '@/components/contacts/ContactCards.vue';
  import { PartnerQuickView } from '@/components/partners';
  import { useTaskCollectionStore, useTaskStore } from '@/store';

  const taskStore = useTaskStore();
  const taskCollectionStore = useTaskCollectionStore();
  const { currentTask } = storeToRefs(taskCollectionStore);
  const isSubmitting = ref(false);
  const errorMessage = ref<string | undefined>();
  const forceTaskTableRefresh = inject<() => void>('forceTaskTableRefresh');

  const partnerId = ref<string | null>(currentTask?.value?.partner_id ?? null);
  const partner = ref<Partner | null>(null);
  const showConfirmDialog = ref<boolean>(false);
  const PartnerContactsRef = ref();

  const openConfirmDialog = () => {
    showConfirmDialog.value = true;
  };

  const closeConfirmDialog = () => {
    showConfirmDialog.value = false;
  };

  const handleConfirmClick = async () => {
    if (!currentTask?.value) return;

    isSubmitting.value = true;
    errorMessage.value = undefined;

    try {
      const email = form.values?.email;

      const contactParams = new Contact({
        email,
        family_name: form.values?.familyName,
        given_name: form.values?.givenName,
      });

      const response = await stuut.tasks.addNewContact(currentTask?.value, contactParams);

      toast.success('Successfully added contact');

      if (partnerId.value) {
        partner.value = await stuut.partners.retrieve(partnerId.value);

        const newContact = partner.value.contacts.find(
          (contact) => contact.email === email,
        ) as Contact;

        partner.value.contacts = [
          newContact,
          ...partner.value.contacts.filter((contact) => contact.email !== email),
        ];
      }

      taskStore.updateAllTasks(currentTask.value);

      return response;
    } catch (err: any) {
      errorMessage.value = err.clientMessage;
      toast.error(err.clientMessage || 'An error occurred');

      if (err.stuutErrorCode === 'TaskActionValidationError' && err.statusCode === 400) {
        await stuut.tasks.retrieve(currentTask?.value?.id, currentTask?.value);
      }
    } finally {
      closeConfirmDialog();
      forceTaskTableRefresh?.();
      isSubmitting.value = false;
    }
  };

  const form = useForm({
    initialValues: {
      email: currentTask?.value?.contactData?.data?.email_address ?? '',
      familyName: currentTask?.value?.contactData?.data?.family_name ?? '',
      givenName: currentTask?.value?.contactData?.data?.given_name ?? '',
    },
    validationSchema: toTypedSchema(
      zod.object({
        email: zod.string().email().min(1),
        familyName: zod.string().optional(),
        givenName: zod.string().optional(),
      }),
    ),
  });

  const onSubmit = (e) => {
    e.preventDefault();

    if (isSubmitting.value) return;

    isSubmitting.value = true;

    form
      .validate()
      .then(async (result) => {
        if (!(result.valid && result.values)) return;

        openConfirmDialog();
      })
      .finally(() => (isSubmitting.value = false));
  };

  onBeforeMount(async () => {
    if (partnerId.value) {
      partner.value = await stuut.partners.retrieve(partnerId.value);
    }
  });

  defineExpose({
    isSubmitting: isSubmitting.value,
    submit: onSubmit,
  });
</script>

<template>
  <div class="grid grid-cols-2 gap-x-10">
    <div class="space-y-6">
      <div class="space-y-2.5">
        <div class="text-sm font-bold">Recommended Contact Details:</div>

        <PartnerQuickView v-if="partner?.id" :partner="partner" />
      </div>

      <form class="grid gap-6 relative" @submit="onSubmit">
        <p v-if="errorMessage" class="text-sm text-left text-destructive">
          {{ errorMessage }}
        </p>

        <FormField v-slot="{ componentField }" name="givenName">
          <FormItem>
            <FormLabel class="font-semibold"> First Name</FormLabel>

            <FormControl>
              <Input
                type="text"
                v-bind="componentField"
                :disabled="!currentTask?.isOpen"
                placeholder="First name"
              />
            </FormControl>
          </FormItem>
        </FormField>

        <FormField v-slot="{ componentField }" name="familyName">
          <FormItem>
            <FormLabel class="font-semibold"> Last Name </FormLabel>

            <FormControl>
              <Input
                type="text"
                v-bind="componentField"
                :disabled="!currentTask?.isOpen"
                placeholder="Last name"
              />
            </FormControl>
          </FormItem>
        </FormField>

        <FormField
          v-slot="{ componentField }"
          :validate-on-input="false"
          :disabled="!currentTask?.isOpen"
          name="email"
        >
          <FormItem>
            <FormLabel class="font-semibold">Email</FormLabel>

            <FormControl>
              <Input
                type="email"
                v-bind="componentField"
                :disabled="true"
                placeholder="name@example.com"
                auto-capitalize="none"
                auto-complete="email"
                auto-correct="off"
              />
            </FormControl>
          </FormItem>
        </FormField>
      </form>

      <AlertDialog
        :open="showConfirmDialog"
        @update:open="(status) => (showConfirmDialog = status)"
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Add contact</AlertDialogTitle>
            <AlertDialogDescription>
              <span>Are you sure you want to add this contact?</span>
            </AlertDialogDescription>
          </AlertDialogHeader>

          <AlertDialogFooter>
            <AlertDialogCancel @click="closeConfirmDialog"> Cancel </AlertDialogCancel>

            <AlertDialogAction class="bg-agent text-white" @click="handleConfirmClick">
              Add contact
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>

    <Card>
      <CardTitle class="text-sm font-bold">Current Contacts</CardTitle>
      <CardContent>
        <ContactCards
          v-if="partner?.id"
          ref="PartnerContactsRef"
          :class="!currentTask?.isOpen && 'opacity-60'"
          :partner="partner"
          mode="external"
        />
      </CardContent>
    </Card>
  </div>
</template>

<style scoped></style>
